import React, { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import MediaQuery from 'react-responsive';
import 'react-datepicker/dist/react-datepicker.css';
import { ClientDateHandler } from '../clientDateHandler/ClientDateHandler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import InputLabel from '../inputLabel/InputLabel';
import { loadPopovers } from './Popovers';
import { useTranslation } from 'react-i18next';

export type DateInputProps = {
    /** The name of the component */
    name: string;
    /** The date value of the component */
    value: string | Date | null;
    /** The error text if validation fails*/
    error?: string;
    /** The label text if it's displayed*/
    label?: string;
    /** The placeholder text when no value has been entered*/
    placeholder?: string;
    /** The dateFormat text based on the language*/
    dateFormat?: string;
    /** The class style for the help button*/
    helpStyle?: string;
    /** The text to appear for help*/
    helpContent?: string;
    /** Decides if the Date Input is disabled or not */
    readOnly?: boolean;
    /** Will reset the time when the date is picked, time isn't required */
    forceTimeReset?: boolean;
    /** If the date will be treated as json. Will only work if the value passed is also a string */
    useJson?: boolean;
    /** Decides if a help block should appear */
    help?: boolean;
    /** Sets the pixel number for when the calendar goes in a portal modal*/
    displayMinWidth?: number;
    /** The minimum date that can be selected on the calender */
    minimumDate?: Date;
    /** The maximum date that can be selected on the calender */
    maximumDate?: Date;
    /** A function to decide what happens when the date changes */
    onDateChanged: (date: string) => void;
};

/** An input component specifically designed to select or type dates on a calendar that is responsive based on a width break */
const DateInput = ({
    name,
    value,
    error,
    label,
    helpStyle,
    helpContent,
    readOnly,
    forceTimeReset,
    useJson = false,
    help,
    displayMinWidth = 400,
    minimumDate,
    maximumDate,
    onDateChanged
}: DateInputProps): JSX.Element => {
    useEffect(() => {
        loadPopovers();
        if (readOnly) return;

        if (forceTimeReset) {
            let localDate: Date | undefined | null = null;
            if (useJson && typeof value === 'string')
                localDate = getDateFromJSON(value);
            else localDate = value as Date;

            const updatedDate = updateTime(localDate);

            const shouldUpdate = updatedDate != localDate || localDate == null;
            if (shouldUpdate) {
                callOnChange(updatedDate);
            }
        }
    }, []);

    const updateTime = (date: Date | null) => {
        if (date != null && typeof date == 'object') {
            const hours = date.getHours();
            if (hours === 0) {
                date.setHours(12); // set to midday ..
            }
        }

        return date;
    };

    const callOnChange = (date: Date) => {
        let dateValue = null;
        if (useJson) {
            const dateJson = setDateToJSON(date);
            dateValue = dateJson;
        } else {
            dateValue = date;
        }

        onDateChanged(dateValue);
    };

    const onDateChange = (date: Date) => {
        if (forceTimeReset) {
            date = updateTime(date);
        }

        callOnChange(date);
    };

    const getDateFromJSON = (jsonText: string): Date => {
        const cdh = new ClientDateHandler();
        cdh.parseJSON(jsonText);

        const date = cdh.getLocalDate();
        return date;
    };

    const setDateToJSON = (date: Date) => {
        const cdh = new ClientDateHandler();
        cdh.setDateValue(date, false);

        const json = cdh.getJSON();
        return json;
    };

    const createLabel = (): JSX.Element => {
        if (label !== undefined) {
            return <InputLabel labelText={label} name={name} />;
        }
        return <></>;
    };

    //Old return logic
    let date: null | Date = null;
    if (useJson) {
        date = getDateFromJSON(value as string);
    } else date = value as Date;

    let wrapperClass = 'form-group';
    if (error && error.length > 0) wrapperClass += ' has-error';
    const { t } = useTranslation();

    const days: string[] = [];
    for (let i = 1; i < 8; i++) {
        days.push(t('date_picker_day_' + i));
    }

    const months: string[] = [];
    for (let i = 1; i < 13; i++) {
        months.push(t('date_picker_month_' + i));
    }

    const dateFormatValue = new ClientDateHandler().getDateFormatString(
        navigator.language
    );
    const locale = {
        localize: {
            day: (n: string | number) => days[n],
            month: (n: string | number) => months[n]
        },
        formatLong: {
            date: () => dateFormatValue
        }
    };

    const today = t('date_picker_today');

    const dateControl = (
        <>
            <MediaQuery maxWidth={displayMinWidth}>
                <DatePicker
                    id={name}
                    className='form-control'
                    dateFormat={dateFormatValue}
                    todayButton={today}
                    placeholderText={dateFormatValue.toUpperCase()}
                    selected={date}
                    onChange={onDateChange}
                    readOnly={readOnly}
                    minDate={minimumDate}
                    maxDate={maximumDate}
                    locale={locale}
                    withPortal
                    autoComplete='off'
                    aria-describedby='button-addon1'
                    portalId='root-portal'
                />
            </MediaQuery>
            <MediaQuery minWidth={displayMinWidth + 1}>
                <DatePicker
                    id={name}
                    className='form-control'
                    dateFormat={dateFormatValue}
                    todayButton={today}
                    placeholderText={dateFormatValue.toUpperCase()}
                    selected={date}
                    onChange={onDateChange}
                    readOnly={readOnly}
                    minDate={minimumDate}
                    maxDate={maximumDate}
                    locale={locale}
                    autoComplete='off'
                    aria-describedby='button-addon1'
                    portalId='root-portal'
                />
            </MediaQuery>
        </>
    );

    let controlClasses = 'field date input-group';
    if (readOnly) controlClasses += ' readOnly';

    let appendBlock = <></>;
    if (help) {
        appendBlock = (
            <a
                id='button-addon1'
                className={`btn btn-sm btn-primary ${helpStyle}`}
                title={label}
                data-toggle='popover'
                data-trigger='focus'
                data-html='true'
                aria-label='help-block'
                data-content={helpContent}
                tabIndex={0}>
                <FontAwesomeIcon icon={faQuestion} />
            </a>
        );
    }

    const labelControl = createLabel();

    return (
        <div className={wrapperClass}>
            {labelControl}
            <div className={controlClasses}>
                {dateControl}
                {appendBlock}
            </div>
            <div className='input'>{error}</div>
        </div>
    );
};

export default DateInput;
